import React, { useState, useEffect } from 'react';
import { FileText, Sparkles, CheckCircle, Clock, User, Briefcase, GraduationCap, Code, Languages, Award, Coffee, Zap } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useLanguage } from '@/context/LanguageContext';

const ANALYSIS_STEPS_FR = [
  { id: 'reading', label: 'Lecture du document', icon: FileText, duration: 3000 },
  { id: 'identity', label: 'Extraction de vos informations personnelles', icon: User, duration: 2500 },
  { id: 'experience', label: 'Analyse de vos expériences professionnelles', icon: Briefcase, duration: 4000 },
  { id: 'education', label: 'Identification de vos formations', icon: GraduationCap, duration: 2500 },
  { id: 'skills', label: 'Détection de vos compétences', icon: Code, duration: 3000 },
  { id: 'languages', label: 'Reconnaissance des langues', icon: Languages, duration: 2000 },
  { id: 'scoring', label: 'Calcul de votre score et analyse IA', icon: Award, duration: 4000 },
  { id: 'preparing', label: 'Préparation de recommandations personnalisées', icon: Sparkles, duration: 3000 },
];

const ANALYSIS_STEPS_EN = [
  { id: 'reading', label: 'Reading the document', icon: FileText, duration: 3000 },
  { id: 'identity', label: 'Extracting your personal information', icon: User, duration: 2500 },
  { id: 'experience', label: 'Analyzing your professional experiences', icon: Briefcase, duration: 4000 },
  { id: 'education', label: 'Identifying your education', icon: GraduationCap, duration: 2500 },
  { id: 'skills', label: 'Detecting your skills', icon: Code, duration: 3000 },
  { id: 'languages', label: 'Recognizing languages', icon: Languages, duration: 2000 },
  { id: 'scoring', label: 'Calculating your score with AI analysis', icon: Award, duration: 4000 },
  { id: 'preparing', label: 'Preparing personalized recommendations', icon: Sparkles, duration: 3000 },
];

// Encouraging messages to show during wait
const WAIT_MESSAGES_FR = [
  { text: "Notre IA analyse en profondeur chaque détail de votre CV", icon: "🔍" },
  { text: "Ce processus prend généralement 1 à 2 minutes", icon: "⏱️" },
  { text: "Vous pouvez quitter cette page, l'analyse continuera en arrière-plan", icon: "📱" },
  { text: "Un CV bien analysé = des recommandations précises", icon: "🎯" },
  { text: "Profitez-en pour préparer votre prochaine étape", icon: "☕" },
  { text: "Bientôt terminé ! Merci pour votre patience", icon: "✨" },
];

const WAIT_MESSAGES_EN = [
  { text: "Our AI is deeply analyzing every detail of your CV", icon: "🔍" },
  { text: "This process typically takes 1-2 minutes", icon: "⏱️" },
  { text: "You can leave this page, the analysis will continue in the background", icon: "📱" },
  { text: "A well-analyzed CV = precise recommendations", icon: "🎯" },
  { text: "Take this time to prepare your next steps", icon: "☕" },
  { text: "Almost done! Thank you for your patience", icon: "✨" },
];

export default function CVAnalysisAnimation({ isAnalyzing, fileName, onComplete, externalProgress = null }) {
  const { language } = useLanguage();
  const ANALYSIS_STEPS = language === 'en' ? ANALYSIS_STEPS_EN : ANALYSIS_STEPS_FR;
  const WAIT_MESSAGES = language === 'en' ? WAIT_MESSAGES_EN : WAIT_MESSAGES_FR;
  const TOTAL_DURATION = ANALYSIS_STEPS.reduce((acc, step) => acc + step.duration, 0);
  const ESTIMATED_TIME = Math.ceil(TOTAL_DURATION / 1000);
  
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [progress, setProgress] = useState(0);
  const [timeRemaining, setTimeRemaining] = useState(ESTIMATED_TIME);
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);

  // Use external progress if provided (from async job)
  useEffect(() => {
    if (externalProgress !== null && externalProgress > 0) {
      setProgress(externalProgress);
      // Calculate which step to show based on external progress
      const stepIndex = Math.min(
        Math.floor((externalProgress / 100) * ANALYSIS_STEPS.length),
        ANALYSIS_STEPS.length - 1
      );
      setCurrentStep(stepIndex);
      // Mark previous steps as completed
      const completed = ANALYSIS_STEPS.slice(0, stepIndex).map(s => s.id);
      setCompletedSteps(completed);
      // Estimate time remaining
      const remainingPercent = (100 - externalProgress) / 100;
      setTimeRemaining(Math.max(0, Math.ceil(TOTAL_DURATION * remainingPercent / 1000)));
    }
  }, [externalProgress, ANALYSIS_STEPS, TOTAL_DURATION]);

  useEffect(() => {
    if (!isAnalyzing) {
      setCurrentStep(0);
      setCompletedSteps([]);
      setProgress(0);
      setTimeRemaining(ESTIMATED_TIME);
      setCurrentMessageIndex(0);
      return;
    }

    // If external progress is being used, animate steps progressively
    if (externalProgress !== null) {
      // Rotate messages
      const messageInterval = setInterval(() => {
        setCurrentMessageIndex(prev => (prev + 1) % WAIT_MESSAGES.length);
      }, 5000);

      // Animate through steps even when backend is fast
      // Minimum ~3s animation so user sees the progress
      const MIN_STEP_DELAY = 600; // ms per step
      let stepTimer;
      const animateSteps = (idx) => {
        if (idx >= ANALYSIS_STEPS.length) {
          // All steps done — now check if backend is also done
          setProgress(100);
          setTimeRemaining(0);
          if (externalProgress >= 100 && onComplete) {
            setTimeout(() => onComplete(), 800);
          }
          return;
        }
        stepTimer = setTimeout(() => {
          setCurrentStep(idx);
          setCompletedSteps(prev => [...new Set([...prev, ...ANALYSIS_STEPS.slice(0, idx).map(s => s.id)])]);
          setProgress(Math.max(externalProgress, Math.round(((idx + 1) / ANALYSIS_STEPS.length) * 90)));
          setTimeRemaining(Math.max(0, ESTIMATED_TIME - (idx + 1) * (MIN_STEP_DELAY / 1000)));
          animateSteps(idx + 1);
        }, MIN_STEP_DELAY);
      };
      animateSteps(0);

      return () => { clearInterval(messageInterval); clearTimeout(stepTimer); };
    }

    let stepIndex = 0;
    let elapsed = 0;

    // Rotate through wait messages every 5 seconds
    const messageInterval = setInterval(() => {
      setCurrentMessageIndex(prev => (prev + 1) % WAIT_MESSAGES.length);
    }, 5000);

    // Progress step by step
    const runStep = () => {
      if (stepIndex >= ANALYSIS_STEPS.length) {
        setProgress(100);
        setTimeRemaining(0);
        clearInterval(messageInterval);
        if (onComplete) onComplete();
        return;
      }

      setCurrentStep(stepIndex);
      const step = ANALYSIS_STEPS[stepIndex];

      setTimeout(() => {
        setCompletedSteps(prev => [...prev, step.id]);
        elapsed += step.duration;
        setProgress(Math.round((elapsed / TOTAL_DURATION) * 100));
        setTimeRemaining(Math.max(0, Math.ceil((TOTAL_DURATION - elapsed) / 1000)));
        stepIndex++;
        runStep();
      }, step.duration);
    };

    runStep();

    return () => {
      clearInterval(messageInterval);
    };
  }, [isAnalyzing, onComplete, ANALYSIS_STEPS, TOTAL_DURATION, ESTIMATED_TIME, WAIT_MESSAGES.length, externalProgress]);
  
  // Handle case where backend finishes AFTER animation steps complete
  useEffect(() => {
    if (externalProgress !== null && externalProgress >= 100 && isAnalyzing && progress >= 100 && onComplete) {
      const timer = setTimeout(() => onComplete(), 800);
      return () => clearTimeout(timer);
    }
  }, [externalProgress, isAnalyzing, progress, onComplete]);

  if (!isAnalyzing) return null;

  return (
    <div className="fixed inset-0 bg-[#081B19]/90 backdrop-blur-md flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full overflow-hidden animate-in fade-in zoom-in-95 duration-300">
        {/* Header with gradient */}
        <div className="bg-gradient-to-br from-[#5BA946] via-[#4a9339] to-[#3d7a2f] p-6 text-white relative overflow-hidden">
          {/* Animated background circles */}
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute -top-4 -right-4 w-24 h-24 bg-white/10 rounded-full animate-pulse" />
            <div className="absolute -bottom-8 -left-8 w-32 h-32 bg-white/5 rounded-full animate-pulse delay-500" />
          </div>
          
          <div className="relative flex items-center gap-4 mb-5">
            <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center backdrop-blur-sm shadow-lg">
              <FileText className="w-7 h-7" />
            </div>
            <div>
              <h3 className="font-bold text-xl">
                {language === 'en' ? 'Analysis in progress' : 'Analyse en cours'}
              </h3>
              <p className="text-sm text-white/80 truncate max-w-[220px]">{fileName}</p>
            </div>
          </div>
          
          {/* Estimated time banner */}
          <div className="relative bg-white/15 rounded-xl p-3 mb-4 backdrop-blur-sm">
            <div className="flex items-center gap-2 text-white/90">
              <Coffee className="w-4 h-4 shrink-0" />
              <span className="text-sm">
                {language === 'en' 
                  ? 'This analysis takes 1-2 minutes. You can leave this page - we\'ll save your results!'
                  : 'Cette analyse prend 1-2 minutes. Vous pouvez quitter - vos résultats seront sauvegardés !'}
              </span>
            </div>
          </div>
          
          {/* Progress bar with glow effect */}
          <div className="relative space-y-3">
            <div className="h-3 bg-white/20 rounded-full overflow-hidden shadow-inner">
              <div 
                className="h-full bg-white rounded-full transition-all duration-500 ease-out relative"
                style={{ width: `${progress}%` }}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/50 to-transparent animate-shimmer" />
              </div>
            </div>
            
            {/* Time and progress info */}
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2 bg-white/20 rounded-full px-3 py-1.5 backdrop-blur-sm">
                <Clock className="w-4 h-4" />
                <span className="text-sm font-medium">
                  {timeRemaining > 0 
                    ? (language === 'en' ? `~${timeRemaining}s remaining` : `~${timeRemaining}s restantes`)
                    : (language === 'en' ? 'Done!' : 'Terminé !')}
                </span>
              </div>
              <span className="text-2xl font-bold">{progress}%</span>
            </div>
          </div>
        </div>

        {/* Steps list */}
        <div className="p-5 max-h-[350px] overflow-y-auto bg-gray-50/50">
          <div className="space-y-2">
            {ANALYSIS_STEPS.map((step, index) => {
              const isComplete = completedSteps.includes(step.id);
              const isCurrent = currentStep === index && !isComplete;
              const isPending = index > currentStep;
              
              return (
                <div 
                  key={step.id}
                  className={cn(
                    "flex items-center gap-3 p-3 rounded-xl transition-all duration-300",
                    isComplete && "bg-green-50 border border-green-100",
                    isCurrent && "bg-blue-50 border border-blue-200 shadow-sm",
                    isPending && "opacity-40"
                  )}
                >
                  <div className={cn(
                    "w-10 h-10 rounded-xl flex items-center justify-center transition-all shadow-sm",
                    isComplete && "bg-[#5BA946] text-white",
                    isCurrent && "bg-blue-500 text-white",
                    isPending && "bg-gray-200 text-gray-400"
                  )}>
                    {isComplete ? (
                      <CheckCircle className="w-5 h-5" />
                    ) : isCurrent ? (
                      <step.icon className="w-5 h-5 animate-pulse" />
                    ) : (
                      <step.icon className="w-5 h-5" />
                    )}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <p className={cn(
                      "font-medium text-sm truncate",
                      isComplete && "text-green-700",
                      isCurrent && "text-blue-700",
                      isPending && "text-gray-400"
                    )}>
                      {step.label}
                    </p>
                    {isCurrent && (
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <div className="flex gap-1">
                          <span className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                          <span className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                          <span className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                        </div>
                        <span className="text-xs text-blue-500">En cours...</span>
                      </div>
                    )}
                  </div>
                  
                  {isComplete && (
                    <span className="text-[#5BA946] font-bold text-sm">✓</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer with rotating messages */}
        <div className="border-t bg-gradient-to-r from-gray-50 to-white p-4">
          <div className="text-center">
            <div className="flex items-center justify-center gap-2 mb-2 transition-all duration-500">
              <span className="text-xl">{WAIT_MESSAGES[currentMessageIndex].icon}</span>
              <span className="text-sm text-gray-700 font-medium">
                {WAIT_MESSAGES[currentMessageIndex].text}
              </span>
            </div>
            <p className="text-xs text-gray-500 flex items-center justify-center gap-1">
              <Zap className="w-3 h-3 text-[#5BA946]" />
              <span className="font-semibold text-[#5BA946]">
                {language === 'en' ? 'Your AI CV coach' : 'Votre coach CV IA'}
              </span>
              <span>{language === 'en' ? 'is working for you' : 'travaille pour vous'}</span>
            </p>
          </div>
        </div>
      </div>
      
      {/* Add shimmer animation keyframes */}
      <style>{`
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        .animate-shimmer {
          animation: shimmer 1.5s infinite;
        }
      `}</style>
    </div>
  );
}
